/* eslint-disable @typescript-eslint/no-magic-numbers */
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, type ReactElement } from 'react';

import DialogBox4, { DialogBox4Reference } from '@/components/DialogBox4';
import Loader from '@/components/Loader';
import Splash from '@/components/Splash';
import useLanguage from '@/hooks/useLanguage';
import useDialog from '@/hooks/util/useDialog';
import { CenterValidationFormData } from '@/pages/CenterValidation/components/CenterValidationDialog';
import ConfirmationDialog from '@/pages/CenterValidation/components/ConfirmationDialog';
import useCenterValidation from '@/pages/CenterValidation/hooks/useCenterValidation';
import { isApiResponseSuccess } from '@/utils/helper';

import AtHomeScreen from '../AtHomeScreen';

export default function CenterValidation(): ReactElement {
  const { isOpen, openDialog, closeDialog, dialogProperties, onClose } = useDialog();
  const { errors, isSubmitting, onSubmitCenterDetails, onProceed, resetValidation } =
    useCenterValidation();

  const dialogReference = useRef<DialogBox4Reference>(null);
  const onDismiss = useCallback(() => {
    if (dialogReference.current && dialogReference.current) dialogReference.current?.onDismiss();
  }, []);

  const {
    openLanguageDialog,
    isLanguageDialogOpen,
    dialogProperties: languageDialogProperties,
    isApplyingLanguage,
  } = useLanguage(onDismiss, true);

  const onHandleBack = closeDialog;

  useEffect(() => {
    const timer = setTimeout(() => {
      const storedLanguage = localStorage.getItem('selectedLanguage');
      if (!storedLanguage) {
        openLanguageDialog();
      }
    }, 2500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitCenterDetailsHelper = (data: CenterValidationFormData) =>
    onSubmitCenterDetails(data, (response) => {
      if (isApiResponseSuccess(response)) {
        posthog.register({
          'program-code': response.data.programCode,
        });
        openDialog(
          <ConfirmationDialog
            validationResponse={response.data}
            onBack={onHandleBack}
            onProceed={(validationResponse) => onProceed(validationResponse, data)}
          />,
        );
      }
    });

  return (
    <div className="h-screen w-screen overflow-hidden">
      <div className="relative h-full w-full">
        <Splash />
        <AtHomeScreen
          errors={errors}
          isSubmitting={isSubmitting}
          resetValidation={resetValidation}
          onSubmitCenterDetails={onSubmitCenterDetailsHelper}
        />
      </div>
      {isOpen && (
        <DialogBox4 open={isOpen} onClose={onClose} variant="default" showCloseIcon>
          {dialogProperties.component}
        </DialogBox4>
      )}
      {isLanguageDialogOpen && (
        <DialogBox4
          open={isLanguageDialogOpen}
          variant="blue"
          ref={dialogReference}
          showCloseIcon={false}
          onClose={languageDialogProperties.onClose}
        >
          {languageDialogProperties.component}
        </DialogBox4>
      )}
      {isApplyingLanguage && <Loader />}
    </div>
  );
}
